/**
 * useGeoEvidence — forked from vCIP_droom_4.0/src/media/useGeoEvidence.ts (S5-9) and re-targeted
 * at API_CONTRACT_SV_v0.1 §2 `POST /geo` (the G1 device-fix leg).
 *
 * What changed against the droom, and why:
 *   • A denial / timeout / unsupported device still POSTS — a fix-less row with `permissionState`
 *     (the be accepts it and derives the source-IP leg from the request), so the reviewer sees
 *     "location: denied" and the network evidence is not discarded with the GPS fix. The same
 *     state also rides on `POST /telemetry.permissions.geolocation`.
 *   • This hook itself never blocks: it writes the visit's G1 row (with coordinates, or the honest
 *     absence). Since US-03482 P1-3 (decided 2026-11-02) the SHOTS wait behind flow/LocationGate.tsx
 *     until the phone gives one fix — on Android the permission can be "Allow" while the phone's
 *     Location is off, and every shot used to bind no position.
 *   • Mock mode is NOT special-cased: the mock adapter implements /geo like the wire.
 *
 * US-03482 G2 (iPhone black-video RCA, 2026-11-03): no request of its own — the visit's G1 row is written from the
 * capture's ONE shared position watch (capture/useGeoWatch.ts, owned by CaptureFlow): the first fix of this visit (or the
 * one the location gate accepted — shotGeo.isCurrentFix) becomes the row with coordinates; an error before any fix
 * becomes the honest fix-less row (once), and a fix that arrives later still gets its row. While the watch says the
 * browser's question may still be on screen (`asking`, §16i) nothing is failed and no row is written. (The droom's
 * explainer-first `gate` mode and its share / retry / continue-without controls were never used here and are gone; the
 * location gate is flow/LocationGate.tsx.)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { postGeo } from '@/api/capture';
import { SvApiError } from '@/api/errors';
import { isCurrentFix, isVisitFix, type GeoFix } from '@/capture/shotGeo';
import { useLatestFix } from '@/capture/useLatestFix';
import type { GeoWatchState } from '@/capture/useGeoWatch';
import { queryPermissionState } from './permissions';

/** The shared watch's position timeout… */
export const GEO_TIMEOUT_MS = 20_000;
/** …and its watchdog fires this much later: some browsers never call back at all, and nothing may wait on them forever. */
export const GEO_WATCHDOG_GRACE_MS = 2_000;
/**
 * While the site permission reads `prompt` the browser's own question may be on screen, and `timeout` does not run
 * until it is answered (Geolocation spec) — so the watchdog must not either: a customer reading the question for 25 s
 * is not "Location off". A watch opened then gets this long ceiling instead of timeout + grace.
 */
export const GEO_PROMPT_CEILING_MS = 60_000;

/** The geolocation permission state, or null when the engine cannot say within a second (never a reason to wait). */
export function permissionSoon(): Promise<PermissionState | null> {
  return new Promise((resolve) => {
    const guard = window.setTimeout(() => resolve(null), 1_000);
    void queryPermissionState('geolocation').then((state) => {
      window.clearTimeout(guard);
      resolve(state);
    });
  });
}

const POST_RETRY_DELAY_MS = 2_500;

export type GeoPhase =
  /** Not asked yet (before the visit has booted). */
  | 'idle'
  | 'requesting'
  /** The ceiling passed with the permission still `prompt`: the question may still be up — no row yet (§16i). */
  | 'asking'
  | 'posting'
  | 'captured'
  | 'denied'
  | 'degraded'
  | 'post-failed'
  | 'resolved';

export type GeoPermissionReport = 'GRANTED' | 'DENIED' | 'TIMEOUT' | 'UNSUPPORTED' | 'ERROR' | 'PENDING';

export interface GeoEvidenceState {
  readonly phase: GeoPhase;
  readonly geoRowId: string | null;
  /** What telemetry should say about the geolocation permission. */
  readonly permission: GeoPermissionReport;
}

export function useGeoEvidence(enabled: boolean, watch: GeoWatchState): GeoEvidenceState {
  const [phase, setPhase] = useState<GeoPhase>('idle');
  const [geoRowId, setGeoRowId] = useState<string | null>(null);
  const [permission, setPermission] = useState<GeoPermissionReport>('PENDING');
  const disposed = useRef(false);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
    };
  }, []);

  useEffect(() => {
    if (!enabled) setPhase('resolved');
  }, [enabled]);

  const record = useCallback(async (f: GeoFix, attempt = 0) => {
    setPhase('posting');
    try {
      const body = {
        lat: f.lat,
        lng: f.lng,
        accuracyM: f.accuracyM,
        permissionState: 'GRANTED' as const,
        provider: 'browser-geolocation',
        collectedAt: f.collectedAt,
        mockFlag: f.mockFlag,
        ...(f.altitudeM !== undefined ? { altitudeM: f.altitudeM } : {}),
        ...(f.speedMps !== undefined ? { speedMps: f.speedMps } : {}),
      };
      const res = await postGeo(body);
      if (disposed.current) return;
      setGeoRowId(res.geoRowId);
      setPhase('captured');
      window.setTimeout(() => {
        if (!disposed.current) setPhase('resolved');
      }, 900);
    } catch (e) {
      if (disposed.current) return;
      if (e instanceof SvApiError && e.status === 401) {
        setPhase('post-failed');
        return;
      }
      if (attempt === 0) {
        window.setTimeout(() => {
          if (!disposed.current) void record(f, 1);
        }, POST_RETRY_DELAY_MS);
        return;
      }
      setPhase('post-failed');
    }
  }, []);

  /** The honest MISSING row — no coordinates, the reason why. Best-effort, one retry, never blocks. */
  const recordMissing = useCallback(async (state: Exclude<GeoPermissionReport, 'GRANTED' | 'PENDING'>, attempt = 0) => {
    try {
      const res = await postGeo({ permissionState: state, provider: 'browser-geolocation', collectedAt: new Date().toISOString(), mockFlag: null });
      if (!disposed.current) setGeoRowId(res.geoRowId);
    } catch {
      if (disposed.current || attempt > 0) return;
      window.setTimeout(() => {
        if (!disposed.current) void recordMissing(state, 1);
      }, POST_RETRY_DELAY_MS);
    }
  }, []);

  // The row comes from the shared watch. A fix of this visit — or one the gate accepted although its own clock calls
  // it older than the visit (shotGeo.acceptHeldFix) — is the row with coordinates.
  const held = useLatestFix();
  const rowFix = isVisitFix(held) || isCurrentFix(held) ? held : null;
  const rowWithFix = useRef(false);
  const rowMissing = useRef(false);
  const watchError = watch.error;
  const watchAsking = watch.asking;
  useEffect(() => {
    if (!enabled) return;
    if (rowFix) {
      if (rowWithFix.current) return;
      rowWithFix.current = true;
      setPermission('GRANTED');
      void record(rowFix);
      return;
    }
    if (rowWithFix.current) return; // a fix is on record; a later hiccup of the watch changes nothing here
    if (watchError === null) {
      // §16i: the browser's question may still be up — no failure is declared and no row is written; its answer decides
      setPhase(watchAsking ? 'asking' : 'requesting');
      return;
    }
    const report: Exclude<GeoPermissionReport, 'GRANTED' | 'PENDING'> =
      watchError === 'denied' ? 'DENIED' : watchError === 'timeout' ? 'TIMEOUT' : watchError === 'unsupported' ? 'UNSUPPORTED' : 'ERROR';
    setPermission(report);
    setPhase(report === 'DENIED' ? 'denied' : 'degraded');
    if (!rowMissing.current) {
      rowMissing.current = true;
      void recordMissing(report);
    }
  }, [enabled, rowFix, watchError, watchAsking, record, recordMissing]);

  return { phase, geoRowId, permission };
}
